import React from 'react';
import { Grid, TextInput, Checkbox, Textarea, Select as MantineSelect, SegmentedControl, Text, Center, Paper } from '@mantine/core';
import { Field, useFormikContext } from 'formik';
import { getConsumerInfoName, getOrderName } from '../../../pages/public/Landing/WithdrawalSchema';
import { AppTheme } from '../../../appTheme';
import { convertToDropDownOptions } from '../../../util/form';
import { propertyOf } from '../../../util';
import { type Item } from '../../../redux/entities/inventory/item';
import { type Category } from '../../../redux/entities/inventory/category';
import { type Order } from '../../../redux/entities/inventory/order';
import OrderItemInput from "../../util/form-components/OrderItemInput/OrderItemInput";
import {useSelector} from "react-redux";
import {CommonState} from "../../../redux";
import {Program, programStore} from "../../../redux/entities/inventory/program";
import {House, houseStore} from "../../../redux/entities/inventory/house";
import {IconHome, IconBuildingWarehouse} from "@tabler/icons-react";

type Props = {
  items: Item[];
  categories: Category[];
  title?: string;
  showWarehouseQuantity?: boolean;
  thinnerCategories?: boolean;
  forceShowBothQuantities?: boolean;
};

/**
 * Reusable Order form used by the public Landing page and internal OrderModal.
 * Assumes it is rendered inside a Formik<Order> provider.
 */
const OrderForm: React.FC<Props> = ({ items, categories, showWarehouseQuantity = false, thinnerCategories = false, title = 'Items to Withdraw', forceShowBothQuantities = false }) => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext<Order>();
  const programs = useSelector((s: CommonState) => programStore.selectors.unarchived(s));
  const houses = useSelector((s: CommonState) => houseStore.selectors.unarchived(s));

  const programOptions = convertToDropDownOptions(programs as any, propertyOf<Program>('name'), propertyOf<Program>('id'));
  const houseOptions = convertToDropDownOptions(houses as any, propertyOf<House>('name'), propertyOf<House>('id'));

  return (
    <Grid gutter="xl" style={{ marginTop: '1rem' }}>
      <Grid.Col span={12} lg={4}>
        <Grid gutter="md">
          <Grid.Col span={12}>
            <h2 style={{ color: AppTheme.colors.themeGreen }}>Contact Information</h2>
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <Field name={getConsumerInfoName('name')}>
              {({ field }: any) => (
                <TextInput
                  label="Your name"
                  {...field}
                  error={touched.consumerInformation?.name && (errors.consumerInformation as any)?.name}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <Field name={getConsumerInfoName('jobTitle')}>
              {({ field }: any) => (
                <TextInput
                  label="Job title"
                  {...field}
                  error={touched.consumerInformation?.jobTitle && (errors.consumerInformation as any)?.jobTitle}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <Field name={getConsumerInfoName('emailAddress')}>
              {({ field }: any) => (
                <TextInput
                  type="email"
                  label="Email address"
                  {...field}
                  error={touched.consumerInformation?.emailAddress && (errors.consumerInformation as any)?.emailAddress}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <Field name={getConsumerInfoName('phoneNumber')}>
              {({ field }: any) => (
                <TextInput
                  label="Phone number"
                  {...field}
                  error={touched.consumerInformation?.phoneNumber && (errors.consumerInformation as any)?.phoneNumber}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <MantineSelect
              label="Program or Department"
              placeholder="Select a program"
              searchable
              clearable
              nothingFound="No programs found"
              withinPortal
              data={programOptions as any}
              value={values.programId || null}
              onChange={(val) => {
                setFieldValue(getOrderName('programId'), val ?? '');
                setFieldTouched(getOrderName('programId'), true);
              }}
              error={touched.programId && (errors as any).programId}
            />
          </Grid.Col>

          <Grid.Col span={12} sm={6}>
            <MantineSelect
              label="House or Neighborhood"
              placeholder="Select a house"
              searchable
              clearable
              nothingFound="No houses found"
              withinPortal
              data={houseOptions as any}
              value={values.houseId || null}
              onChange={(val) => {
                setFieldValue(getOrderName('houseId'), val ?? '');
                setFieldTouched(getOrderName('houseId'), true);
              }}
              error={touched.houseId && (errors as any).houseId}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Field name={getConsumerInfoName('everythingFound')}>
              {({ field }: any) => (
                <Checkbox
                  label="Did you find all the items you were looking for?"
                  checked={!!field.value}
                  onChange={(e) => field.onChange({ target: { name: field.name, value: e.currentTarget.checked } })}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12}>
            <Field name={getConsumerInfoName('everythingFoundComment')}>
              {({ field }: any) => (
                <Textarea
                  label="If not, please tell us what you couldn't find (optional)"
                  placeholder="Share any details to help us improve availability"
                  autosize
                  minRows={2}
                  maxRows={4}
                  disabled={values.consumerInformation?.everythingFound}
                  {...field}
                />
              )}
            </Field>
          </Grid.Col>

          <Grid.Col span={12}>
            <Field name={getConsumerInfoName('additionalComment')}>
              {({ field }: any) => (
                <Textarea
                  label="Additional comments (optional)"
                  placeholder="Anything else you'd like us to know"
                  autosize
                  minRows={2}
                  maxRows={4}
                  {...field}
                />
              )}
            </Field>
          </Grid.Col>
        </Grid>
      </Grid.Col>

      <Grid.Col span={12} lg={8}>
        <OrderItemInput
          items={items}
          categories={categories}
          title={title}
          showStoreQuantity={true}
          showWarehouseQuantity={forceShowBothQuantities}
          thinnerCategories={thinnerCategories}
          hideSourceSelector={forceShowBothQuantities}
        />
      </Grid.Col>
    </Grid>
  );
};

export default OrderForm;
